import { Plus, Tray } from '@phosphor-icons/react';
import { Tip } from '~/components/ui/tip';
import { cn } from '~/lib/utils';
import { IS_APPLE } from './is-apple';

/**
 * Single source for the combo shown on the button — swap the shortcut here
 * and every render of the badge follows. The window keydown handler that
 * actually fires it lives in `chat-top-bar.tsx` — keep the two in sync if
 * the combo ever changes.
 *
 * ⌥N rather than the design's ⌘N: browsers reserve Cmd/Ctrl+N for "new
 * window" and never deliver it to the page, so that badge would be a lie
 * (design doc decision #6 — the badge must never show a combo that does
 * nothing).
 */
export const NEW_CHAT_SHORTCUT_LABEL = IS_APPLE ? '⌥N' : 'Alt+N';

interface NewChatButtonProps {
  onClick: () => void;
  className?: string;
  /**
   * A new chat is waiting for the "which window?" pick: this click starts it
   * in the background instead, and the button says so (eddy, 2026-10-26).
   */
  background?: boolean;
}

/**
 * The most prominent element in the bar, by intent (README §1). Brand-blue
 * fill, a plus icon, and — at default size — the keyboard-shortcut badge.
 */
export function NewChatButton({ onClick, className, background = false }: NewChatButtonProps) {
  const button = (
    <button
      type="button"
      onClick={onClick}
      data-background={background ? 'true' : undefined}
      aria-label={background ? 'Start the new chat in the background' : undefined}
      className={cn(
        // 38, not the handoff's 42 (Mattias, 2026-08-14): the button sat
        // taller than everything beside it. 38 matches the status cluster and
        // the ask chip exactly, so the whole left group reads as one row.
        'eddy-chat-btn inline-flex h-[38px] shrink-0 items-center gap-[10px] rounded-[11px] bg-[var(--eddy-blue)] px-[18px]',
        'text-[16px] font-semibold tracking-[-0.015em] whitespace-nowrap text-white',
        'shadow-[0_5px_14px_-6px_rgba(41,98,255,0.7)] transition-[background-color,box-shadow] duration-150',
        'hover:bg-[var(--eddy-blue-hover)] hover:shadow-[0_8px_20px_-8px_rgba(41,98,255,0.85)]',
        className,
      )}
    >
      {background ? <Tray size={15} /> : <Plus size={15} />}
      {/* 2026-09-01: "Chat", not "New chat". The plus glyph already says
       *  "new", and the button now sits beside a "Build" one — "New chat"
       *  next to "Build" reads as two different kinds of label. */}
      Chat
      <span className="eddy-kbd rounded-[6px] border border-white/25 bg-white/20 px-[6px] py-[2px] font-mono text-xs leading-tight font-medium text-white">
        {NEW_CHAT_SHORTCUT_LABEL}
      </span>
    </button>
  );
  return background ? <Tip label="Start the new chat in the background — it waits in the top bar">{button}</Tip> : button;
}
